"use client";

import clsx from "clsx";
import Image from "next/image";
import React from "react";

interface CommonCardProps {
  title: string; // required
  number?: number | string; // optional
  description?: string; // optional
  highlight?: string; // optional
  showLeftBorder?: boolean; // optional
  iconSrc?: string; // optional image/icon path
  iconAlt?: string; // optional alt text for icon
  icon?: React.ComponentType<{ className?: string }>; // optional react-icon component
  contentAlign?: "left" | "center" | "right"; // new prop
}

const CommonCard: React.FC<CommonCardProps> = ({
  number,
  title,
  description,
  highlight,
  showLeftBorder = true,
  iconSrc,
  iconAlt = "Card Icon",
  icon: IconComponent,
  contentAlign = "left", // default to left
}) => {
  return (
    <div
      className={clsx(
        "border bg-[#fff]/70  border-[#e5e5e5]/48 hover:scale-105 transition-all duration-800 hover:bg-white p-4 lg:p-6 2xl:py-12 w-full h-full text-[#1b1b1b] relative",
        !showLeftBorder && "border-l-0",
        contentAlign === "center" && "text-center",
        contentAlign === "right" && "text-right"
      )}
    >
      {/* Faded Number */}
      {number !== undefined && number !== "" && (
        <div
          className={clsx(
            "text-5xl font-semibold text-[#000]/10 leading-none z-0 select-none",
            contentAlign === "center" && "mx-auto",
            contentAlign === "right" && "ml-auto"
          )}
        >
          {number}
        </div>
      )}

      {/* Optional Icon */}
      {(iconSrc || IconComponent) && (
        <div
          className={clsx(
            " flex",
            contentAlign === "center" && "justify-center",
            contentAlign === "right" && "justify-end",
            contentAlign === "left" && "justify-start"
          )}
        >
          {IconComponent ? (
            <IconComponent className="h-10 w-10 xl:h-12 xl:w-12  text-[#272d57]" />
          ) : (
            <Image
              src={iconSrc!}
              alt={iconAlt}
              width={0}  // fixed default
              height={0} // fixed default
              className="object-contain h-10 w-10 xl:h-12 xl:w-12 2xl:h-14 2xl:w-14  "
            />
          )}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 space-y-2 mt-2 lg:mt-4">
        <h3 className="text-md md:text-lg  font-bold text-[#272d57]">{title}</h3>

        {description && (
          <p className="text-sm sm:text-base text-[#1b1b1b] leading-snug">{description}</p>
        )}

        {highlight && (
          <p className="text-[#D53333] italic font-bold pt-2 lg:pt-6">
            <span className="text-[#D53333] font-bold">{'//'}</span> {highlight}
          </p>
        )}
      </div>
    </div>
  );
};

export default CommonCard;
